/* eslint-disable security/detect-non-literal-fs-filename */
import { call, put, delay } from '@redux-saga/core/effects'
import { select } from 'redux-saga/effects'
import { utils, writeFileXLSX } from 'xlsx'
import dayjs from 'dayjs'
import {
  BRAND_ACTION_ENUM,
  IBrandProductMapping,
  IBrandSummaryState,
  IFetchBrandReportPayload,
  IFetchBrandSummaryActionPayload,
  SelectedDataKey
} from 'state/new-summary/brand/brand.types'
import {
  fetchBrandProductMapping,
  fetchBrandReportSuccessAction,
  fetchBrandSummaryDataSuccessAction,
  setBrandProductMapping,
  setBrandSaleActiveFilterAction,
  setFilterDataSuccess,
  setLoaderAction,
  setOverallBrandSummaryAction,
  setPaginationData
} from 'state/new-summary/brand/brand.actions'
import {
  getBrandProductMappingApi,
  getBrandReportApi,
  getBrandSummaryApi,
  uploadBrandProductMappingApi
} from 'services/Api/StaffLinkApiFolder/api'
import {
  formatDataForExcel,
  getExcelHeading,
  getFormattedBrandReportSummary,
  getFormattedBrandSummary,
  getOverallBrandSaleSummary
} from 'state/new-summary/brand/brand.helpers'
import { Action } from 'infra/types'
import { IDashboardState } from 'state/dashboard/dashboard.types'
import { IAppState } from 'infra/AppState'
import { getOrderColumnWidths } from 'state/orderSummary/orderSummary.helpers'
import { addAutoFadeNotification } from 'pages/Notification/Notification.actions'
import { NotificationType } from 'pages/Notification/Notification.types'
import { API_RESPONSE_TYPE, IApiResponse } from '../../../constants'

export function* fetchBrandSummaryEffect(action: Action<IFetchBrandSummaryActionPayload>) {
  const brandSummaryResponse: IApiResponse = yield call(getBrandSummaryApi, {
    from_date: action.payload.startTime,
    to_date: action.payload.endTime
  })
  if (brandSummaryResponse.type === API_RESPONSE_TYPE.FAILURE) {
    throw new Error('Could not fetch summary. Please try again after some time.', {
      cause: 'customError'
    })
  }
  const formattedBrandSummary = getFormattedBrandSummary(brandSummaryResponse?.data?.summary)
  yield put(fetchBrandSummaryDataSuccessAction(formattedBrandSummary))
  yield put(setOverallBrandSummaryAction(getOverallBrandSaleSummary(formattedBrandSummary)))
  yield put(setBrandSaleActiveFilterAction(action.payload))
  yield put(setLoaderAction({ id: 'outerGroupSummary', value: false }))
}

export function* fetchBrandProductMappingEffect() {
  yield put(setLoaderAction({ id: 'fetchingProductMapping', value: true }))
  const response: IApiResponse<IBrandProductMapping> = yield call(getBrandProductMappingApi)
  if (response.type === API_RESPONSE_TYPE.FAILURE) {
    throw new Error('Could not fetch Product Mapping. Please try again after some time.', {
      cause: 'customError'
    })
  }

  const allProductCode = new Set<string>()
  const allProductName = new Set<string>()
  const productCodeToProductIdMapper = new Map<string, string>()
  const productNameToProductIdMapper = new Map<string, string[]>()

  response.data.products.forEach((product) => {
    allProductCode.add(product.product_code)
    allProductName.add(product.product_name)
    productCodeToProductIdMapper.set(product.product_code, product.id)
    productNameToProductIdMapper.set(product.product_name, [
      ...(productNameToProductIdMapper.get(product.product_name) || []),
      product.id
    ])
  })

  yield put(
    setBrandProductMapping({
      brandProductMapping: response.data.products,
      allProductCode,
      allProductName,
      productCodeToProductIdMapper,
      productNameToProductIdMapper
    })
  )
  yield put(setLoaderAction({ id: 'fetchingProductMapping', value: false }))

  const {
    loaders: { uploadingProductMapping }
  } = yield select((app: IAppState) => app.Brand)
  if (uploadingProductMapping) {
    yield put(setLoaderAction({ id: 'uploadingProductMapping', value: false }))
  }
}

export function* uploadBrandProductMappingEffect(action: Action<{ formData: FormData }>) {
  const { payload } = action

  yield put(setLoaderAction({ id: 'uploadingProductMapping', value: true }))
  const response: IApiResponse = yield call(uploadBrandProductMappingApi, payload.formData)
  if (response.type === API_RESPONSE_TYPE.FAILURE) {
    throw new Error('Something went Wrong', {
      cause: 'customError'
    })
  }

  if (response.type === API_RESPONSE_TYPE.SUCCESS) {
    yield put(
      addAutoFadeNotification({
        type: NotificationType.SUCCESS,
        bodyText: response?.data?.message || 'File Uploaded successfully!!!'
      })
    )

    yield put(
      addAutoFadeNotification({
        type: NotificationType.INFO,
        bodyText: 'Refetching Product Mapping Table Data !!!'
      })
    )
    yield delay(3000)
    yield put(fetchBrandProductMapping())
  }
}

export function* fetchBrandReportEffect(action: Action<IFetchBrandReportPayload>) {
  const { merchantProfile, brandData }: IDashboardState = yield select(
    (app: IAppState) => app.Dashboard
  )
  const { page, pageSize }: IBrandSummaryState = yield select((app: IAppState) => app.Brand)
  const merchantId = merchantProfile?.data?.id || ''
  const brandReportResponse: IApiResponse = yield call(getBrandReportApi, {
    from_date: action.payload.fromDate,
    to_date: action.payload.toDate,
    ...(action.payload.distributorId !== ''
      ? { distributor_id: action.payload.distributorId }
      : {}),
    format: action.payload.format,
    brand_id: brandData[merchantId].brand?.id,
    ...(action.payload.filters && {
      filters: {
        ...(action.payload.filters.distributor_ids && {
          distributor_ids: action.payload.filters.distributor_ids
        }),
        ...(action.payload.filters.brand_inv_ids && {
          brand_inv_ids: action.payload.filters.brand_inv_ids
        })
      }
    }),
    page: action.payload?.paginationData?.page || page,
    page_size: action.payload?.paginationData?.pageSize || pageSize
  } as any)
  if (brandReportResponse.type === API_RESPONSE_TYPE.FAILURE) {
    throw new Error('Could not fetch summary. Please try again after some time.', {
      cause: 'customError'
    })
  }

  yield put(
    fetchBrandReportSuccessAction(
      getFormattedBrandReportSummary(brandReportResponse?.data?.sale_reports)
    )
  )
  yield put(
    setBrandSaleActiveFilterAction({
      filterType: action.payload.filterType,
      startTime: action.payload.fromDate,
      endTime: action.payload.toDate
    })
  )

  yield put(
    setPaginationData({
      page: action.payload.paginationData?.page || page,
      pageSize: action.payload.paginationData?.pageSize || pageSize,
      totalCount: brandReportResponse.data.pagination_detail.total_count
    })
  )

  yield put(setLoaderAction({ id: 'outerGroupSummary', value: false }))
}

export function* setFilterPaginationDataEffect(
  action: Action<{
    selectedDataKey: SelectedDataKey
    selectedDataValues: string[]
    paginationData?: {
      page: number
      pageSize: number
      totalCount: number
    }
  }>
) {
  const {
    filteredData: previousSelectedFilteredData,
    productCodeToProductIdMapper,
    productNameToProductIdMapper,
    activeFilter,
    page,
    pageSize
  }: IBrandSummaryState = yield select((app: IAppState) => app.Brand)
  const { brandData, merchantProfile }: IDashboardState = yield select(
    (app: IAppState) => app.Dashboard
  )
  const {
    currentRoute: {
      navParams: { distributorId }
    }
  } = yield select((app: IAppState) => app.Navigation)
  const merchantId = merchantProfile.data?.id || ''
  let distributorIds: string[] = []
  let inventoryIds: string[] = []
  const utilisedBranchName: any = {}
  if (action.payload.selectedDataKey === 'branchFilter') {
    action.payload.selectedDataValues.forEach((branchName) => {
      if (!(branchName in utilisedBranchName)) {
        distributorIds = [
          ...distributorIds,
          ...(brandData[merchantId]?.distributorBranchToDistributorIdMap?.[branchName] || [])
        ]
      }
    })
  } else {
    previousSelectedFilteredData.branchFilter.forEach((branchName) => {
      if (brandData[merchantId]?.distributorBranchToDistributorIdMap?.[branchName]) {
        utilisedBranchName[branchName] = true
        distributorIds = [
          ...distributorIds,
          ...(brandData[merchantId]?.distributorBranchToDistributorIdMap?.[branchName] || [])
        ]
      }
    })
  }

  const utilisedRegionName: any = {}
  if (action.payload.selectedDataKey === 'regionFilter') {
    action.payload.selectedDataValues.forEach((regionName) => {
      if (!(regionName in utilisedRegionName)) {
        distributorIds = [
          ...distributorIds,
          ...(brandData[merchantId]?.distributorRegionToDistributorIdMap?.[regionName] || [])
        ]
      }
    })
  } else {
    previousSelectedFilteredData.regionFilter.forEach((regionName) => {
      if (brandData[merchantId]?.distributorRegionToDistributorIdMap?.[regionName]) {
        utilisedRegionName[regionName] = true
        distributorIds = [
          ...distributorIds,
          ...(brandData[merchantId]?.distributorRegionToDistributorIdMap?.[regionName] || [])
        ]
      }
    })
  }

  const utilisedDistributorName: any = {}
  if (action.payload.selectedDataKey === 'distributorNameFilter') {
    action.payload.selectedDataValues.forEach((distributorName) => {
      if (!(distributorName in utilisedDistributorName)) {
        distributorIds = [
          ...distributorIds,
          ...(brandData[merchantId]?.distributorNameToDistributorIdMap?.[distributorName] || [])
        ]
      }
    })
  } else {
    previousSelectedFilteredData.distributorNameFilter.forEach((distributorName) => {
      if (brandData[merchantId]?.distributorNameToDistributorIdMap?.[distributorName]) {
        utilisedDistributorName[distributorName] = true
        distributorIds = [
          ...distributorIds,
          ...(brandData[merchantId]?.distributorNameToDistributorIdMap?.[distributorName] || [])
        ]
      }
    })
  }

  const utilizedProductCode: any = {}
  if (action.payload.selectedDataKey === 'productCodeFilter') {
    action.payload.selectedDataValues.forEach((productCode) => {
      if (!(productCode in utilizedProductCode)) {
        inventoryIds = [
          ...inventoryIds,
          (productCodeToProductIdMapper.get(productCode) || '') as string
        ]
      }
    })
  } else {
    previousSelectedFilteredData.productCodeFilter.forEach((productCode) => {
      if (productCodeToProductIdMapper.get(productCode)) {
        utilizedProductCode[productCode] = true
        inventoryIds = [
          ...inventoryIds,
          (productCodeToProductIdMapper.get(productCode) || '') as string
        ]
      }
    })
  }

  const utilizedProductNames: any = {}
  if (action.payload.selectedDataKey === 'productDescriptionFilter') {
    action.payload.selectedDataValues.forEach((productName) => {
      if (
        !(productName in utilizedProductNames) &&
        productNameToProductIdMapper.get(productName) &&
        productNameToProductIdMapper.get(productName)?.length
      ) {
        inventoryIds = [...inventoryIds, ...(productNameToProductIdMapper.get(productName) || [])]
      }
    })
  } else {
    previousSelectedFilteredData.productDescriptionFilter.forEach((productName) => {
      if (
        productNameToProductIdMapper.get(productName) &&
        productNameToProductIdMapper.get(productName)?.length
      ) {
        utilizedProductNames[productName] = true
        inventoryIds = [...inventoryIds, ...(productNameToProductIdMapper.get(productName) || [])]
      }
    })
  }

  const brandReportResponse: IApiResponse = yield call(getBrandReportApi, {
    from_date: activeFilter?.startTime,
    to_date: activeFilter?.endTime,
    distributor_id: '',
    ...(distributorId !== 'overall' ? { distributor_id: distributorId } : {}),
    format: BRAND_ACTION_ENUM.JSON,
    brand_id: brandData[merchantId].brand?.id,
    filters: {
      ...(distributorIds.length && { distributor_ids: distributorIds }),
      ...(inventoryIds.length && { brand_inv_ids: inventoryIds })
    },
    page: action.payload?.paginationData?.page || 1,
    page_size: action.payload?.paginationData?.pageSize || 10
  } as any)

  if (brandReportResponse.type === API_RESPONSE_TYPE.FAILURE) {
    yield put(
      addAutoFadeNotification({
        type: NotificationType.ERROR,
        bodyText: 'Something went wrong, Filter not applied!!!'
      })
    )
    return
  }
  yield put(
    fetchBrandReportSuccessAction(
      getFormattedBrandReportSummary(brandReportResponse?.data?.sale_reports)
    )
  )
  yield put(
    setPaginationData({
      page: action.payload.paginationData?.page || 1,
      pageSize: action.payload.paginationData?.pageSize || 10,
      totalCount: brandReportResponse.data.pagination_detail.total_count
    })
  )
  yield put(setFilterDataSuccess(action.payload))
}

export function* downloadBrandReportEffect(action: Action<IFetchBrandReportPayload>) {
  const { brandReportItems, activeFilter }: IBrandSummaryState = yield select(
    (app: IAppState) => app.Brand
  )
  const title = 'Brand Report Data'
  const formattedData = formatDataForExcel(brandReportItems || [])
  const ws = utils.json_to_sheet(formattedData, { origin: 'A5' })
  const wb = utils.book_new()
  const excelHeading = activeFilter ? getExcelHeading(activeFilter, title) : title
  utils.sheet_add_aoa(
    ws,
    [
      [excelHeading],
      [],
      [`Summary Data Created On: ${dayjs.unix(dayjs().unix()).format('DD MMM, YYYY')}`],
      []
    ],
    {
      origin: 'A1'
    }
  )

  if (formattedData.length) {
    ws['!cols'] = getOrderColumnWidths(formattedData)
  }

  utils.book_append_sheet(wb, ws, 'Sheet 1')
  writeFileXLSX(wb, 'overall_summary_data.xlsx')
  yield put(
    setBrandSaleActiveFilterAction({
      filterType: action.payload.filterType,
      startTime: action.payload.fromDate,
      endTime: action.payload.toDate
    })
  )
}

// export function* downloadBrandReportEffect(action: Action<IFetchBrandReportPayload>) {
//   const { merchantProfile, brandData }: IDashboardState = yield select((app: IAppState) => app.Dashboard)
//   const merchantId = merchantProfile?.data?.id || ''
//   const brandReportResponse: IApiResponse = yield call(downloadBrandReportApi, {
//     from_date: action.payload.fromDate,
//     to_date: action.payload.toDate,
//     ...(action.payload.distributorId !=='' ? {distributor_id: action.payload.distributorId} : {}),
//     format: action.payload.format,
//     brand_id: brandData[merchantId].brand?.id
//   })
//   if (brandReportResponse.type === API_RESPONSE_TYPE.FAILURE) {
//     throw new Error('Could not fetch summary. Please try again after some time.', {
//       cause: 'customError'
//     })
//   }

//   // Convert the API response to a Blob (assuming the API response contains binary data for the file)
//   const blob = new Blob([brandReportResponse.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

//   // Trigger file download using FileSaver (or create an anchor element and trigger the download manually)
//   saveAs(blob, 'brand_report.xlsx');  // 'brand_report.xlsx' is the file name, you can customize it

//   yield put(
//     setBrandSaleActiveFilterAction({
//       filterType: action.payload.filterType,
//       startTime: action.payload.fromDate,
//       endTime: action.payload.toDate
//     })
//   )
// }
